import { useState } from 'react'

import { PageLayout } from '@components/layouts/PageLayout'
import { DialogProvider } from '@components/DialogProvider'
import { toast } from '@stores/toastStore'
import {
  CallingSessionSupportersDataTable,
  type CallingSessionSupporterRow,
} from '@frontend/contexts/users_management/components/CallingSessionSupportersDataTable'
import { ImpactPanel } from '@frontend/contexts/users_management/components/calling-session-dashboard/ImpactPanel'
import { CallingJourneyPanel } from '@frontend/contexts/users_management/components/calling-session-dashboard/CallingJourneyPanel'

import { INITIAL_CALLBACKS, type Callback } from './CallbackRequestsPreview'

const SESSION_ID = 'preview-session'

const QUARTER_HOUR = 15 * 60_000
const hoursAgo = (hours: number) =>
  new Date(Math.round((Date.now() - hours * 3_600_000) / QUARTER_HOUR) * QUARTER_HOUR).toISOString()

const sessionSupporter = (
  id: string,
  firstName: string,
  lastName: string,
  fields: Partial<CallingSessionSupporterRow> = {}
): CallingSessionSupporterRow => ({
  id: `ss-${id}`,
  supporterId: id,
  position: 0,
  status: 'available',
  outcome: null,
  unavailableReason: null,
  unavailableUntil: null,
  lastCallAttemptAt: null,
  actionGroupId: 'g1',
  activeSkip: null,
  supporter: { id, firstName, lastName },
  ...fields,
})

const heldRow = (cb: Callback) => {
  const [firstName, ...rest] = cb.supporter.fullName.split(' ')
  return sessionSupporter(cb.supporter.id, firstName, rest.join(' '), {
    status: 'skipped_callback',
    callback: {
      dueAt: cb.dueAt,
      flexible: cb.flexible,
      assignee: cb.assignee?.name ?? null,
      requestedBy: cb.requestedBy?.name ?? null,
      notes: cb.notes,
    },
  })
}

const [held1, held2, held3, held4, ...heldRest] = INITIAL_CALLBACKS.filter((cb) => !cb.completedAt).map(heldRow)

const INITIAL_CALL_LIST = [
  sessionSupporter('m1', 'Marco', 'Gallo', {
    status: 'completed',
    outcome: 'successful',
    lastCallAttemptAt: hoursAgo(26),
  }),
  sessionSupporter('m2', 'Federica', 'Greco', {
    status: 'completed',
    outcome: 'unsuccessful',
    lastCallAttemptAt: hoursAgo(25),
  }),
  held1,
  sessionSupporter('m3', 'Alessandro', 'Lombardi'),
  sessionSupporter('m4', 'Valentina', 'Moretti', { lastCallAttemptAt: hoursAgo(24) }),
  held2,
  held3,
  sessionSupporter('m5', 'Lorenzo', 'Barbieri'),
  sessionSupporter('m6', 'Martina', 'Fontana'),
  held4,
  sessionSupporter('m7', 'Simone', 'Santoro', { lastCallAttemptAt: hoursAgo(23) }),
  ...heldRest,
].map((row, index) => ({ ...row, position: index + 1 }))

const COMPLETED_CYCLES = [
  {
    id: 'cycle-1',
    cycleNumber: 1,
    startedAt: hoursAgo(27),
    finishedAt: hoursAgo(22),
    completed: true,
    callsAttemptedCount: 7,
    completedSupportersCount: 2,
    successfulCount: 1,
    unsuccessfulCount: 1,
    durationSeconds: 5 * 3600,
  },
]

function CallbackRequestsCallListPreview() {
  const [callList, setCallList] = useState(INITIAL_CALL_LIST)

  const inQueue = callList.filter((row) => row.status !== 'skipped_callback')
  const completed = callList.filter((row) => row.status === 'completed')
  const available = callList.filter((row) => row.status === 'available')

  const addToQueue = (row: CallingSessionSupporterRow) => {
    setCallList((rows) => rows.map((r) => (r.id === row.id ? { ...r, status: 'available' } : r)))
    toast.success(`${row.supporter.firstName} ${row.supporter.lastName} is back in the queue`)
  }

  return (
    <DialogProvider registry={{}}>
      <PageLayout.Root>
        <PageLayout.Header icon="phone-call-01" heading="October volunteer drive" />
        <PageLayout.Body>
          <div className="tw:mb-5 tw:grid tw:grid-cols-1 tw:gap-6 tw:xl:grid-cols-2">
            <ImpactPanel
              callingSessionId={SESSION_ID}
              callInProgressAt={null}
              isPaused={false}
              totalCallsAttemptedCount={7}
              successfulContactsCount={completed.filter((row) => row.outcome === 'successful').length}
              unsuccessfulContactsCount={completed.filter((row) => row.outcome === 'unsuccessful').length}
              completedSupportersCount={completed.length}
              availableSupportersThisCycleCount={available.length}
              sessionProgress={{
                completedCount: completed.length,
                totalSupportersCount: inQueue.length,
                percentage: Math.round((completed.length / inQueue.length) * 100),
              }}
              currentCycle={{ id: 'cycle-2', cycleNumber: 2, startedAt: null, finishedAt: null, completed: false }}
              showViewAllButton={false}
            />
            <CallingJourneyPanel completedCycles={COMPLETED_CYCLES} />
          </div>

          <div className="tw:pb-6">
            <CallingSessionSupportersDataTable
              sessionSupporters={callList}
              callingSessionId={SESSION_ID}
              nextSupporterToCallId={available[0]?.supporterId}
              showOutcome
              onAddToQueue={addToQueue}
            />
          </div>
        </PageLayout.Body>
      </PageLayout.Root>
    </DialogProvider>
  )
}

export default CallbackRequestsCallListPreview
